import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEOHead, { getWebPageSchema, getBreadcrumbSchema } from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { comics, siteInfo } from '@/mocks/comics';

const chapterImages = [
  'https://readdy.ai/api/search-image?query=Elegant%20Chinese%20ink%20painting%20style%20manga%20page%2C%20beautiful%20woman%20in%20traditional%20hanfu%20walking%20through%20an%20ancient%20Chinese%20garden%20with%20blooming%20lotus%20flowers%2C%20soft%20brushwork%2C%20warm%20lighting%2C%20vertical%20composition%2C%20story%20telling%20illustration&width=800&height=1200&seq=chapter-page-01&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Chinese%20manga%20style%20illustration%20of%20a%20mysterious%20young%20man%20in%20dark%20robes%20standing%20in%20a%20moonlit%20courtyard%2C%20ink%20wash%20painting%20style%2C%20atmospheric%20shadows%2C%20ancient%20Chinese%20architecture%2C%20dramatic%20lighting&width=800&height=1200&seq=chapter-page-02&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Beautiful%20Chinese%20manga%20art%20depicting%20a%20tense%20conversation%20between%20two%20characters%20in%20traditional%20Chinese%20clothing%20inside%20an%20ancient%20hall%2C%20delicate%20ink%20lines%2C%20warm%20candlelight%2C%20emotional%20atmosphere&width=800&height=1200&seq=chapter-page-03&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Chinese%20historical%20fantasy%20manga%20page%20showing%20a%20mysterious%20ritual%20scene%20with%20floating%20magical%20symbols%20and%20glowing%20lotus%20flowers%2C%20ancient%20temple%20interior%2C%20ethereal%20blue%20light%2C%20detailed%20ink%20brushwork&width=800&height=1200&seq=chapter-page-04&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Romantic%20Chinese%20manga%20scene%20of%20two%20characters%20sharing%20an%20intimate%20moment%20under%20a%20blooming%20cherry%20blossom%20tree%2C%20soft%20pink%20petals%20falling%2C%20moonlight%2C%20ink%20painting%20style%2C%20warm%20gentle%20tones&width=800&height=1200&seq=chapter-page-05&orientation=portrait',
];

export default function ChapterPage() {
  const { id, chapterId } = useParams<{ id: string; chapterId: string }>();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const comic = comics.find((c) => c.id === id);

  if (!comic) {
    return (
      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
        <h1 className="text-xl font-heading text-foreground-900 mb-2">漫画未找到</h1>
        <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
          返回首页
        </Link>
      </div>
    );
  }

  const currentChapter = comic.chapters.find((ch) => ch.id === chapterId);
  if (!currentChapter) {
    return (
      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
        <h1 className="text-xl font-heading text-foreground-900 mb-2">章节未找到</h1>
        <Link to={`/comic/${comic.id}`} className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
          返回漫画详情
        </Link>
      </div>
    );
  }

  const currentIndex = comic.chapters.findIndex((ch) => ch.id === chapterId);
  const prevChapter = currentIndex < comic.chapters.length - 1 ? comic.chapters[currentIndex + 1] : null;
  const nextChapter = currentIndex > 0 ? comic.chapters[currentIndex - 1] : null;

  const categoryMap: Record<string, string> = {
    guoman: '国产漫画', rexue: '热血', lianai: '恋爱', xuanhuan: '玄幻',
    wuxia: '武侠', kehuan: '科幻', xuanyi: '悬疑', gaoxiao: '搞笑',
    qihuan: '奇幻', dushi: '都市',
  };

  const displayPages = chapterImages.slice(0, currentChapter.pageCount > chapterImages.length ? chapterImages.length : currentChapter.pageCount);

  return (
    <>
      <SEOHead
        title={`${comic.title} ${currentChapter.title} - ${siteInfo.name}`}
        description={`${comic.title}漫画${currentChapter.title}免费阅读，无删减完整版，下拉式流畅观看。吹雪漫画网提供高品质漫画阅读体验。`}
        keywords={`${comic.title},${currentChapter.title},免费阅读,无删减漫画,吹雪漫画网`}
        canonicalUrl={`https://chuixue.manhua.com/comic/${comic.id}/chapter/${currentChapter.id}`}
        schemaData={{
          ...getWebPageSchema(
            `${comic.title} ${currentChapter.title} - ${siteInfo.name}`,
            `${comic.title}漫画${currentChapter.title}免费阅读`,
            `https://chuixue.manhua.com/comic/${comic.id}/chapter/${currentChapter.id}`,
          ),
          ...getBreadcrumbSchema([
            { name: '首页', url: 'https://chuixue.manhua.com/' },
            { name: comic.title, url: `https://chuixue.manhua.com/comic/${comic.id}` },
            { name: currentChapter.title, url: `https://chuixue.manhua.com/comic/${comic.id}/chapter/${currentChapter.id}` },
          ]),
        }}
      />

      <Breadcrumb
        items={[
          { name: '首页', href: '/' },
          { name: comic.title, href: `/comic/${comic.id}` },
          { name: currentChapter.title },
        ]}
      />

      <article className="w-full max-w-[800px] mx-auto px-4 md:px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-lg md:text-xl font-heading text-foreground-900">
              {comic.title}
            </h1>
            <p className="text-sm text-foreground-500 mt-0.5">{currentChapter.title}</p>
          </div>
          <div className="relative">
            <button
              onClick={() => setSettingsOpen(!settingsOpen)}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
              aria-label="设置"
            >
              <i className="ri-settings-3-line text-foreground-600"></i>
            </button>
            {settingsOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 rounded-lg bg-background-50 border border-background-200/70 shadow-sm p-3 z-30">
                <p className="text-xs text-foreground-500 mb-2">阅读设置</p>
                <div className="space-y-2">
                  <button className="w-full text-left px-2 py-1.5 rounded text-xs text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer">
                    <i className="ri-sun-line mr-1.5"></i>日间模式
                  </button>
                  <button className="w-full text-left px-2 py-1.5 rounded text-xs text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer">
                    <i className="ri-moon-line mr-1.5"></i>夜间模式
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-5">
          {comic.categories.map((cat) => (
            <Link
              key={cat}
              to={`/category/${cat}`}
              className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 text-xs hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer"
            >
              {categoryMap[cat] || cat}
            </Link>
          ))}
          <span className="text-xs text-foreground-400">{currentChapter.pageCount}P</span>
        </div>

        <div className="space-y-0">
          {displayPages.map((img, index) => (
            <div key={index} className="w-full bg-background-200">
              <img
                src={img}
                alt={`${comic.title} ${currentChapter.title} 第${index + 1}页`}
                title={`${comic.title} ${currentChapter.title} - 吹雪漫画网免费阅读`}
                className="w-full h-auto"
                loading={index > 2 ? 'lazy' : undefined}
              />
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mt-8 mb-4 pt-6 border-t border-background-200/70">
          {prevChapter ? (
            <Link
              to={`/comic/${comic.id}/chapter/${prevChapter.id}`}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-background-100 text-foreground-600 text-sm hover:bg-background-200 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              上一话
            </Link>
          ) : (
            <div />
          )}

          <Link
            to={`/comic/${comic.id}`}
            className="px-4 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回目录
          </Link>

          {nextChapter ? (
            <Link
              to={`/comic/${comic.id}/chapter/${nextChapter.id}`}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer"
            >
              下一话
              <i className="ri-arrow-right-line"></i>
            </Link>
          ) : (
            <div />
          )}
        </div>
      </article>
    </>
  );
}